/**
 * The overview's cards beside the limits (the days still to confirm are the
 * dashboard's own card, `InOutToConfirm`, with the periods to settle):
 *
 *   • the renewal window of the title in force — its dates, or the rule's note
 *     when the regime words it instead;
 *   • the long-term rights the record counts towards (acquisition), one line
 *     each — the right on the left, its state and the day it completes on
 *     the right; the tolerances, the article and why a right is still being
 *     settled in the ⓘ — no number where the rule is unresolved (AC-2).
 *
 * Both fill the height they are given, so side by side they stand as one row.
 */
import { CalendarDays, Route } from '@tonnie/assets';
import {
  INOUT_ACQUISITION_STATUS_KEY,
  inoutAcquisitionRows,
  type InOutAcquisitionClock,
} from '@tonnie/heroes';
import { useLocale } from '@tonnie/locale';
import type { InOutReading } from '@tonnie/sdk';
import { Badge, Card, CardContent, CardHeader, CardTitle, InfoPopover, cn } from '@tonnie/ui';

import { INOUT_CLASSES } from '../lib/inout-tone';

export function InOutRenewalCard({ status }: { status: InOutReading }) {
  const { t, formatDate } = useLocale();
  const window = status.evaluation.renewalWindow;

  return (
    <Card className="h-full" data-testid="inout-renewal">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarDays className={cn('h-4 w-4', INOUT_CLASSES.text)} aria-hidden="true" />
          {t('inout.renewal.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm text-muted-foreground">
        {window === null ? (
          <p>{t('inout.renewal.none')}</p>
        ) : 'opensOn' in window ? (
          <>
            <p className="text-lg font-semibold tabular-nums text-foreground">
              {t('inout.renewal.window', {
                opens: formatDate(window.opensOn, 'calendarDay'),
                closes: formatDate(window.closesOn, 'calendarDay'),
              })}
            </p>
            <p>{t('inout.renewal.planTrips')}</p>
          </>
        ) : (
          <p>{window.note}</p>
        )}
      </CardContent>
    </Card>
  );
}

function AcquisitionRow({ clock, label }: { clock: InOutAcquisitionClock; label: string }) {
  const { t, formatDate, formatNumber } = useLocale();
  const resolved = clock.status !== 'UNRESOLVED';
  const tolerances = [
    { key: 'cumulative' as const, count: clock.cumulative },
    { key: 'consecutive' as const, count: clock.consecutive },
  ];
  return (
    <li
      className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 py-3 first:pt-0 last:pb-0"
      data-testid="inout-acquisition"
    >
      <p className="text-sm font-semibold text-foreground">
        {label}{' '}
        <InfoPopover
          label={t('inout.acquisition.info', { right: label })}
          data-testid="inout-acquisition-info"
        >
          <p className="font-semibold text-foreground">{label}</p>
          {resolved ? (
            <>
              {clock.completesOn && (
                <p className="text-muted-foreground">
                  {t('inout.acquisition.completesOn', {
                    date: formatDate(clock.completesOn, 'calendarDay'),
                  })}
                </p>
              )}
              {tolerances.map(({ key, count }) =>
                count ? (
                  <p key={key} className="text-muted-foreground">
                    {key === 'cumulative'
                      ? t('inout.acquisition.cumulative', {
                          value: formatNumber(count.value),
                          tolerance: formatNumber(count.tolerance),
                        })
                      : t('inout.acquisition.consecutive', {
                          value: formatNumber(count.value),
                          tolerance: formatNumber(count.tolerance),
                        })}
                  </p>
                ) : null
              )}
            </>
          ) : (
            clock.unresolvedReason && (
              <p className="text-muted-foreground">{clock.unresolvedReason}</p>
            )
          )}
          <p className="text-xs text-muted-foreground">{clock.source}</p>
        </InfoPopover>
      </p>
      <div className="flex flex-col items-end gap-1 text-right">
        <Badge variant="outline" className="font-medium">
          {t(INOUT_ACQUISITION_STATUS_KEY[clock.status])}
        </Badge>
        {resolved && clock.completesOn && (
          <p
            className="text-xs tabular-nums text-muted-foreground"
            data-testid="inout-acquisition-date"
          >
            {t('inout.acquisition.completeOn', {
              date: formatDate(clock.completesOn, 'calendarDay'),
            })}
          </p>
        )}
      </div>
    </li>
  );
}

export function InOutAcquisitionCard({ status }: { status: InOutReading }) {
  const { t } = useLocale();
  // Every acquisition of the rules is named (the country schema requires its
  // `label`), and the route sends the name of each one the engine counted.
  const clocks = inoutAcquisitionRows(status.evaluation.acquisition, status.acquisitions);
  if (clocks.length === 0) return null;

  return (
    <Card className="h-full" data-testid="inout-acquisitions">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Route className={cn('h-4 w-4', INOUT_CLASSES.text)} aria-hidden="true" />
          {t('inout.acquisition.title')}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{t('inout.acquisition.subtitle')}</p>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {clocks.map(({ clock, label }) => (
            <AcquisitionRow key={clock.acquisitionId} clock={clock} label={label} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
